import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { MessageSquare, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useRealtimeMessages } from '@/hooks/useRealtimeMessages';
import { getPushState, enablePush, disablePush } from '@/lib/push';
import ConversationList from '@/components/chat/ConversationList';
import { ChatHeader, PinnedSubject } from '@/components/chat/ChatHeader';
import { MessageBubble, DaySeparator, TypingBubble } from '@/components/chat/MessageBubble';
import Composer from '@/components/chat/Composer';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const POLL_FALLBACK_MS = 20_000;
const TYPING_DEBOUNCE_MS = 1500;

const previewOf = (msg) => {
  if ((msg.content || '').trim()) return msg.content.trim();
  const metas = msg.attachments_meta || [];
  if (metas.some(a => a.kind === 'audio')) return '🎤 Messaggio vocale';
  const pdf = metas.find(a => a.kind === 'pdf');
  if (pdf) return `📄 ${pdf.name}`;
  return `📷 ${(msg.attachments || []).length} foto`;
};

export default function Messages() {
  const { cid } = useParams();
  const [params] = useSearchParams();
  const focusMsg = params.get('m');
  const nav = useNavigate();
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [otherTyping, setOtherTyping] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [tab, setTab] = useState('active');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [pushState, setPushState] = useState('off');
  const [confirmBlock, setConfirmBlock] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const scrollRef = useRef(null);
  const lastMsgIdRef = useRef(null);
  const typingTimerRef = useRef(null);
  const myTypingSentAtRef = useRef(0);
  const currentCidRef = useRef(cid);
  currentCidRef.current = cid;

  const current = useMemo(() => convs.find(x => x.conversation_id === cid) || null, [convs, cid]);
  const peerId = current?.other_user?.user_id;
  const blocked = current?.blocked_by_me || current?.blocked_me;

  const loadConvs = useCallback(() => api.get('/conversations').then(r => setConvs(r.data || [])).catch(() => {}), []);
  const loadMessages = useCallback(async (silent = false) => {
    if (!cid) return;
    try {
      const r = await api.get(`/conversations/${cid}/messages`);
      const list = r.data || [];
      const newLast = list.length ? list[list.length - 1].message_id : null;
      if (newLast !== lastMsgIdRef.current || !silent) { setMessages(list); lastMsgIdRef.current = newLast; }
    } catch (_) { /* silent */ }
  }, [cid]);

  useEffect(() => { loadConvs(); getPushState().then(setPushState).catch(() => {}); }, [loadConvs]);
  useEffect(() => {
    setMessages([]); lastMsgIdRef.current = null; setOtherTyping(false);
    if (cid) loadMessages();
  }, [cid, loadMessages]);
  useEffect(() => {
    if (current && current.unread) setConvs(p => p.map(c => c.conversation_id === cid ? { ...c, unread: 0 } : c));
  }, [current, cid]);
  const tabSyncedRef = useRef(null);
  useEffect(() => {
    if (current && tabSyncedRef.current !== cid) { tabSyncedRef.current = cid; setTab(current.archived ? 'archived' : 'active'); }
  }, [current, cid]);

  // Ricerca nei messaggi (debounce)
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => api.get('/messages/search', { params: { q } }).then(r => setResults(r.data || [])).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [query]);

  const handleWsEvent = useCallback((ev) => {
    if (ev.type === 'message') {
      const msg = ev.data;
      if (msg.conversation_id === currentCidRef.current) {
        setMessages(prev => {
          if (prev.some(m => m.message_id === msg.message_id)) return prev;
          const idx = prev.findIndex(m => m._pending && m.sender_id === msg.sender_id && (m.content || '') === (msg.content || '')
                                          && (m.attachments || []).length === (msg.attachments || []).length);
          if (idx >= 0) { const copy = [...prev]; copy[idx] = msg; return copy; }
          return [...prev, msg];
        });
        lastMsgIdRef.current = msg.message_id;
        if (msg.sender_id !== user?.user_id) { wsSendRef.current?.({ type: 'read', conversation_id: msg.conversation_id }); setOtherTyping(false); }
      }
      setConvs(prev => {
        const i = prev.findIndex(c => c.conversation_id === msg.conversation_id);
        if (i < 0) { loadConvs(); return prev; }
        const base = prev[i];
        const inChat = msg.conversation_id === currentCidRef.current;
        const updated = { ...base, last_message: previewOf(msg), updated_at: msg.created_at, archived: false,
                          unread: inChat ? 0 : (base.unread || 0) + (msg.sender_id !== user?.user_id ? 1 : 0) };
        return [updated, ...prev.filter((_, j) => j !== i)];
      });
    } else if (ev.type === 'typing') {
      if (ev.conversation_id === currentCidRef.current) {
        setOtherTyping(true);
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setOtherTyping(false), 3500);
      }
    } else if (ev.type === 'read') {
      if (ev.conversation_id === currentCidRef.current && ev.by !== user?.user_id) {
        setMessages(prev => prev.map(m => m.sender_id === user?.user_id ? { ...m, read: true } : m));
      }
    } else if (ev.type === 'presence') {
      setOnlineUsers(prev => { const n = new Set(prev); if (ev.online) n.add(ev.user_id); else n.delete(ev.user_id); return n; });
    }
  }, [user, loadConvs]);

  const { connected: wsConnected, send: wsSend } = useRealtimeMessages({ onEvent: handleWsEvent });
  const wsSendRef = useRef(wsSend);
  wsSendRef.current = wsSend;

  useEffect(() => { if (wsConnected && cid) wsSend({ type: 'read', conversation_id: cid }); }, [wsConnected, cid, wsSend]);

  // Presenza iniziale del peer
  useEffect(() => {
    if (!peerId) return;
    api.get(`/presence/${peerId}`).then(r => setOnlineUsers(p => { const n = new Set(p); if (r.data.online) n.add(peerId); else n.delete(peerId); return n; })).catch(() => {});
  }, [peerId]);

  // Polling di sicurezza se il WS è giù
  useEffect(() => {
    if (wsConnected || !cid) return undefined;
    const iv = setInterval(() => { loadMessages(true); loadConvs(); }, POLL_FALLBACK_MS);
    return () => clearInterval(iv);
  }, [cid, wsConnected, loadMessages, loadConvs]);

  const notifyTyping = useCallback(() => {
    if (!wsConnected || !peerId || !cid) return;
    const now = Date.now();
    if (now - myTypingSentAtRef.current < TYPING_DEBOUNCE_MS) return;
    myTypingSentAtRef.current = now;
    wsSend({ type: 'typing', conversation_id: cid, to: peerId });
  }, [wsConnected, peerId, cid, wsSend]);

  // Scroll: al messaggio cercato oppure in fondo
  useEffect(() => {
    if (focusMsg && messages.some(m => m.message_id === focusMsg)) {
      const el = document.getElementById(`msg-${focusMsg}`);
      const box = scrollRef.current;
      if (el && box) box.scrollTo({ top: el.offsetTop - box.clientHeight / 2 + el.offsetHeight / 2, behavior: 'smooth' });
      setHighlight(focusMsg);
      const t = setTimeout(() => setHighlight(null), 2600);
      return () => clearTimeout(t);
    }
    const el = scrollRef.current;
    if (!el) return undefined;
    const toBottom = () => el.scrollTo({ top: el.scrollHeight });
    toBottom();
    const t = setTimeout(toBottom, 400);
    return () => clearTimeout(t);
  }, [messages.length, focusMsg, otherTyping]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async ({ content, metas, voiceDuration }) => {
    if (!current?.other_user) return false;
    const tempId = `tmp_${Date.now()}`;
    setMessages(m => [...m, { message_id: tempId, sender_id: user.user_id, content, attachments: metas.map(a => a.file_id),
                               attachments_meta: metas, created_at: new Date().toISOString(), _pending: true }]);
    try {
      await api.post('/messages', {
        receiver_id: current.other_user.user_id, listing_id: current.listing_id, batch_id: current.batch_id,
        content, attachments: metas.map(a => a.file_id), voice_duration: voiceDuration,
      });
      if (!wsConnected) { await loadMessages(); loadConvs(); }
      return true;
    } catch (e) {
      setMessages(m => m.filter(x => x.message_id !== tempId));
      toast.error(formatApiError(e));
      return false;
    }
  };

  const toggleArchive = async () => {
    const archived = !current.archived;
    try {
      await api.post(`/conversations/${cid}/archive`, { archived });
      setConvs(p => p.map(c => c.conversation_id === cid ? { ...c, archived } : c));
      toast.success(archived ? 'Chat archiviata' : 'Chat ripristinata');
      setTab('active');
      if (archived) nav('/messaggi');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setBlocked = async (value) => {
    try {
      if (value) await api.post(`/users/${peerId}/block`); else await api.delete(`/users/${peerId}/block`);
      setConvs(p => p.map(c => c.other_user?.user_id === peerId ? { ...c, blocked_by_me: value } : c));
      toast.success(value ? 'Utente bloccato' : 'Utente sbloccato');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePush = async () => {
    if (pushState === 'unsupported') return toast.info('Su iPhone aggiungi AgriNexus alla schermata Home (Condividi → Aggiungi a Home) per ricevere le notifiche.');
    if (pushState === 'denied') return toast.error('Le notifiche sono bloccate: riattivale dalle impostazioni del browser.');
    try {
      if (pushState === 'on') { await disablePush(); setPushState('off'); toast.success('Notifiche disattivate'); }
      else { await enablePush(); setPushState('on'); toast.success('Notifiche attive: ti avviseremo dei nuovi messaggi'); }
    } catch (e) { toast.error(e.message || 'Impossibile attivare le notifiche'); getPushState().then(setPushState).catch(() => {}); }
  };

  return (
    <div className="max-w-7xl mx-auto sm:px-6 lg:px-10 sm:py-6">
      <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] lg:grid-cols-[400px_1fr] h-[calc(100dvh-10.5rem)] sm:h-[calc(100dvh-9.5rem)] min-h-[520px] sm:rounded-3xl border-y sm:border border-border overflow-hidden bg-card/60 backdrop-blur-xl shadow-[0_40px_120px_-40px_rgba(0,0,0,0.6)]"
           data-testid="messages-shell">
        <aside className={`${cid ? 'hidden md:flex' : 'flex'} flex-col min-h-0 min-w-0 border-r border-border bg-card/70`} data-testid="messages-sidebar">
          <ConversationList convs={convs} cid={cid} tab={tab} setTab={setTab} query={query} setQuery={setQuery} results={results}
                            onlineUsers={onlineUsers} wsConnected={wsConnected} pushState={pushState} onTogglePush={togglePush}/>
        </aside>

        <section className={`${cid ? 'flex' : 'hidden md:flex'} flex-col min-h-0 min-w-0 relative`} data-testid="messages-chat">
          {!cid || !current ? (
            <div className="grid place-items-center flex-1 text-center p-10 relative overflow-hidden">
              <div aria-hidden className="absolute w-[420px] h-[420px] rounded-full blur-[120px] opacity-20" style={{ background: 'radial-gradient(circle, rgb(180,255,0), transparent 70%)' }}/>
              <div className="relative">
                <MessageSquare size={46} className="mx-auto text-chartreuse/70 mb-4" strokeWidth={1.2}/>
                <div className="font-display text-2xl mb-1">{cid ? 'Caricamento…' : 'Seleziona una conversazione'}</div>
                <div className="text-sm text-muted-foreground max-w-sm">Le chat con proprietari e acquirenti appaiono qui, in tempo reale.</div>
              </div>
            </div>
          ) : (
            <>
              <ChatHeader conv={current} online={onlineUsers.has(peerId)} typing={otherTyping} wsConnected={wsConnected}
                          onBack={() => nav('/messaggi')} onArchive={toggleArchive}
                          onBlockToggle={() => (current.blocked_by_me ? setBlocked(false) : setConfirmBlock(true))}/>
              <PinnedSubject conv={current}/>
              <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-5 py-4 space-y-2" data-testid="messages-scroll">
                {messages.map((m, i) => {
                  const prev = messages[i - 1];
                  const newDay = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
                  return (
                    <div key={m.message_id}>
                      {newDay && <DaySeparator date={m.created_at}/>}
                      <MessageBubble m={m} mine={m.sender_id === user.user_id} highlighted={highlight === m.message_id}/>
                    </div>
                  );
                })}
                <AnimatePresence>{otherTyping && <TypingBubble name={current.other_user?.name?.split(' ')[0] || 'L\'altro utente'}/>}</AnimatePresence>
              </div>
              {blocked ? (
                <div className="shrink-0 bg-destructive/10 text-destructive text-sm p-4 text-center border-t border-destructive/20 flex items-center justify-center gap-2" data-testid="chat-blocked-banner">
                  <Ban size={14}/>
                  {current.blocked_by_me ? <>Hai bloccato questo utente. <button onClick={() => setBlocked(false)} className="underline font-medium" data-testid="chat-unblock-inline">Sblocca</button></>
                    : 'Non puoi rispondere a questa conversazione.'}
                </div>
              ) : (
                <Composer onSend={send} onTyping={notifyTyping}/>
              )}
            </>
          )}
        </section>
      </div>

      <AlertDialog open={confirmBlock} onOpenChange={setConfirmBlock}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bloccare {current?.other_user?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Non potrà più scriverti e tu non potrai scrivergli. Puoi sbloccarlo in qualsiasi momento dal menu della chat.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="block-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="block-confirm-btn" className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                               onClick={() => setBlocked(true)}>Blocca</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
